import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from '../components/Layout/AuthLayout';

/**
 * SchoolAdminRegisterPage - Registration form for school administrators
 * Uses master activation code from super admin
 * Connects to backend /api/auth/school-admin/register endpoint
 */
const SchoolAdminRegisterPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    masterCode: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { registerSchoolAdmin } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!formData.email || !formData.password || !formData.fullName || !formData.masterCode) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      await registerSchoolAdmin(
        formData.email,
        formData.password,
        formData.fullName,
        formData.masterCode
      );

      setSuccess('School account created successfully! Redirecting to login...');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login/school-admin');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Registration failed. Please check your master activation code and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout showBackButton={true}>
      <form className="w-full" onSubmit={handleSubmit}>
        {/* Icon & Title */}
        <div className="text-center mb-4">
          <div className="relative inline-block mb-3">
            <div className="absolute inset-0 blur-3xl opacity-40 bg-pink-500" />
            <div className="relative bg-gradient-to-br from-pink-500 to-rose-600 rounded-[1.5rem] p-4 shadow-2xl">
              <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1 tracking-tight">
            School Registration
          </h2>
          <p className="text-gray-500 text-sm">
            Set up your school on Brillabee
          </p>
        </div>

        {/* Master Activation Code - Most Important */}
        <div className="form-group mb-3 p-3 bg-pink-50 rounded-xl border-2 border-pink-200">
          <label htmlFor="master-code" className="block mb-2 font-bold text-pink-900 text-xs">
            Master Activation Code*
          </label>
          <input
            type="text"
            id="master-code"
            name="masterCode"
            placeholder="MASTER-XXXXXXXX"
            className="w-full py-2 px-3 border-2 border-pink-300 rounded-lg bg-white focus:outline-none focus:border-pink-500 font-mono text-sm transition-colors"
            value={formData.masterCode}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
          <p className="text-xs text-pink-700 mt-2 flex items-center gap-1">
            <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Provided by Brillabee with subscription
          </p>
        </div>

        <div className="space-y-3 mb-4">
          <div className="form-group">
            <label htmlFor="full-name" className="block mb-1 font-semibold text-gray-800 text-xs">
              Administrator Name*
            </label>
            <input
              type="text"
              id="full-name"
              name="fullName"
              placeholder="Principal John Smith"
              className="w-full py-2 px-3 border-2 border-gray-200 rounded-lg bg-white focus:outline-none focus:border-pink-500 transition-colors text-sm"
              value={formData.fullName}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email" className="block mb-1 font-semibold text-gray-800 text-xs">
              School Email Address*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="admin@school.edu"
              className="w-full py-2 px-3 border-2 border-gray-200 rounded-lg bg-white focus:outline-none focus:border-pink-500 transition-colors text-sm"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password" className="block mb-1 font-semibold text-gray-800 text-xs">
              Password*
            </label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder="Min 8 characters"
              className="w-full py-2 px-3 border-2 border-gray-200 rounded-lg bg-white focus:outline-none focus:border-pink-500 transition-colors text-sm"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="new-password"
              minLength={8}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm-password" className="block mb-1 font-semibold text-gray-800 text-xs">
              Confirm Password*
            </label>
            <input
              type="password"
              id="confirm-password"
              name="confirmPassword"
              placeholder="Re-enter password"
              className="w-full py-2 px-3 border-2 border-gray-200 rounded-lg bg-white focus:outline-none focus:border-pink-500 transition-colors text-sm"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-50 border-2 border-red-200 rounded-lg text-red-700 text-xs text-center">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-3 p-2 bg-green-50 border-2 border-green-200 rounded-lg text-green-700 text-xs text-center font-semibold">
            {success}
          </div>
        )}

        <button
          type="submit"
          className={`w-full bg-gradient-to-r from-pink-600 to-rose-700 text-white font-semibold py-3 px-4 rounded-xl
                     hover:shadow-xl hover:scale-[1.02] transition-all duration-300 text-sm tracking-wide mb-3 ${
            isLoading ? 'opacity-70 cursor-not-allowed' : ''
          }`}
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Register School'}
        </button>

        <div className="mb-3 p-2 bg-blue-50 border-2 border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2">
          <svg className="w-3 h-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>You can create teacher codes and manage your school after logging in.</span>
        </div>

        {/* Divider */}
        <div className="relative my-3">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="px-3 bg-white text-gray-400">Already registered?</span>
          </div>
        </div>

        <Link
          to="/login/school-admin"
          className="block text-center w-full bg-gray-100 text-gray-800 font-semibold py-3 px-4 rounded-xl
                   hover:bg-gray-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 text-sm"
        >
          Sign In
        </Link>
      </form>
    </AuthLayout>
  );
};

export default SchoolAdminRegisterPage;
